import { Button, Card, Col, FullPageSpinner, Row } from '@wyz/ui';
import { useOnMount } from '@wyz/utils';
import { WebStorageStateStore } from 'oidc-client-ts';
import * as React from 'react';
import {
  AuthProviderProps,
  AuthProvider as OidcAuthProvider,
  useAuth,
} from 'react-oidc-context';
import { useNavigate } from 'react-router';
import {
  Trans,
  useConfig,
  useAnalytics,
  AnalyticsProvider,
} from '~/common/context';
import { log } from '~/common/helpers';
import { IUserLoginEvent } from '~/common/types';
import { User } from 'oidc-client-ts';

function KeyCloakAuthProvider(props: React.PropsWithChildren) {
  const config = useConfig();
  const oidcConfig: AuthProviderProps = {
    authority: config.authority,
    client_id: config.clientId,
    redirect_uri:
      location.pathname === '/'
        ? `${location.origin}/post-signin`
        : location.href,
    // Note: Use localStorage to store the user session so it can be shared between tabs
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  };

  return (
    <OidcAuthProvider
      {...oidcConfig}
      stopCheckSessionOnError
      automaticSilentRenew
    >
      {props.children}
    </OidcAuthProvider>
  );
}

function ExpiredSession() {
  const auth = useAuth();
  const navigate = useNavigate();
  const redirectToSignin = () => {
    auth
      .signinRedirect({
        redirect_uri: location.origin,
      })
      .then(() => {
        void navigate('/post-signin');
      })
      .catch((error: Error) => {
        log(error);
        void navigate('/500');
      });
  };

  useOnMount(() => {
    if (!auth.user?.session_state) {
      // eslint-disable-next-line no-console
      console.error(
        'The user is using an old URL with an outdated query string, redirecting to signin',
      );
      auth
        .signinRedirect({
          redirect_uri: `${location.origin}${location.pathname}`,
        })
        .catch(() => {});
    }
  });

  if (!auth.user?.session_state) {
    return <FullPageSpinner />;
  }

  return (
    <Row className={'flex-center min-vh-100 py-6 text-center'}>
      <Col className={'col-sm-10 col-md-8 col-lg-6 col-xl-5 col-xxl-4'}>
        <Card className='card border-0 shadow-xxl w-100'>
          <Card.Body className='card-body p-4 p-sm-5 d-flex flex-column'>
            <Card.Title>
              <Trans i18nKey='app.common.session_expired_title' />
            </Card.Title>
            <p>
              <Trans i18nKey='app.common.session.expired_message' />
            </p>
            <Button
              id={'btn-signin'}
              variant={'primary'}
              onClick={redirectToSignin}
              size={'lg'}
            >
              <Trans i18nKey='app.common.session_expired_button' />
            </Button>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

export function AuthChecker(props: React.PropsWithChildren) {
  const auth = useAuth();

  switch (auth.activeNavigator) {
    case 'signinSilent':
      return (
        <FullPageSpinner>
          <h4>
            <Trans i18nKey='app.common.in_progress_sign_in' />
          </h4>
        </FullPageSpinner>
      );
    case 'signoutRedirect':
      return (
        <FullPageSpinner>
          <h4>
            <Trans i18nKey='app.common.in_progress_sign_out' />
          </h4>
        </FullPageSpinner>
      );
    default:
      break;
  }

  if (auth.isLoading && auth.activeNavigator !== 'signoutSilent') {
    return <FullPageSpinner />;
  }

  if (auth.isLoading && auth.activeNavigator === 'signoutSilent') {
    return (
      <FullPageSpinner>
        <h4>
          <Trans i18nKey='app.common.in_progress_sign_out' />
        </h4>
      </FullPageSpinner>
    );
  }

  if (auth.error) {
    return <ExpiredSession />;
  }

  return <>{props.children}</>;
}

function AuthLoader(props: React.PropsWithChildren) {
  const auth = useAuth();
  const { sendEvent } = useAnalytics();
  const sentRef = React.useRef(false);

  React.useEffect(() => {
    if (!auth.events) return;

    // Handler triggered only during interactive authentication (not on simple refresh/token renew)
    const handler = (user: User | null | undefined) => {
      // Check if the session has changed (new interactive authentication)
      if (
        !sentRef.current &&
        user?.profile?.sid &&
        typeof window !== 'undefined'
      ) {
        const lastSid = window.sessionStorage.getItem('last_login_sid');
        if (lastSid !== user.profile.sid) {
          window.sessionStorage.setItem('user_id', user.profile.sub);
          sendEvent(user, {
            type: 'user_login_event',
            email: user.profile.email,
          } as IUserLoginEvent);
          window.sessionStorage.setItem('last_login_sid', user.profile.sid);
          sentRef.current = true;
        }
      }
    };

    auth.events.addUserLoaded(handler);
    return () => {
      auth.events.removeUserLoaded(handler);
    };
  }, [auth.events, sendEvent]);

  if (auth.isLoading) {
    return <FullPageSpinner />;
  }

  return <>{props.children}</>;
}

export function AuthProvider(props: React.PropsWithChildren) {
  return (
    <KeyCloakAuthProvider>
      <AnalyticsProvider>
        <AuthLoader>{props.children}</AuthLoader>
      </AnalyticsProvider>
    </KeyCloakAuthProvider>
  );
}
